import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, noop } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { AuthUsecase, DistinctSubject, recursiveQuery } from '@daikin-tic/dxone-com-lib';

import { FileQueryParams, ResultQueryParams, Results } from '../models/result.model';
import { ResultGateway } from '../usecases/result.gateway';
import { ResultUsecase } from '../usecases/result.usecase';

@Injectable()
export class ResultInteractor extends ResultUsecase {
  get results$(): Observable<Results> {
    return this._results;
  }

  private readonly _results = new DistinctSubject<Results>(new Results());

  constructor(
    private _authUsecase: AuthUsecase,
    private _resultGateway: ResultGateway,
  ) {
    super();

    this._authUsecase.authState$
      .pipe(
        map(({ status }) => status === 'signedIn'),
        distinctUntilChanged(),
      )
      .subscribe(signedIn => (signedIn ? noop : this.onSignOut()));
  }

  listResults(params: ResultQueryParams): Observable<never> {
    const result = new AsyncSubject<never>();
    recursiveQuery(queryParams => this._resultGateway.listResults(queryParams), params).subscribe({
      next: results => this._results.next(new Results(results)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  getFile(params: FileQueryParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._resultGateway.getFile(params).subscribe({
      next: ({ url }) => result.next(url),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  clearResults(): void {
    this._results.next(new Results());
  }

  private onSignOut(): void {
    this._results.next(new Results());
  }
}
